import { NavigateFunction } from "react-router-dom"
import ApiHandler from "src/api/ApiHandler"

/**
 * Navigation service for class components to use React Router navigation
 * without needing to be wrapped in hooks.
 */
class NavigationService {
    private navigate: NavigateFunction | null = null

    setNavigate(navigate: NavigateFunction) {
        this.navigate = navigate
    }

    navigateTo(path: string, replace: boolean = false) {
        if (this.navigate) {
            this.navigate(path, { replace })
        } else {
            // Fallback to window.location if navigate not set (shouldn't happen in normal flow)
            window.location.href = path
        }
    }

    navigateToHome() {
        this.navigateTo("/")
    }

    navigateToLogin() {
        this.navigateTo("/login")
    }

    navigateToContact() {
        this.navigateTo("/contact")
    }

    navigateToTerms() {
        this.navigateTo("/terms")
    }

    navigateToSavedValuations() {
        this.navigateTo("/saved-valuations")
    }

    async navigateToValuationRoute(
        propertyId: string,
        apiHandler: ApiHandler,
        routeBase: string = "/valuate",
        houseCondition?: string,
        removedHomes?: string,
        uniqueId?: string
    ) {
        try {
            const idForUrl = uniqueId ?? propertyId
            const addressQuery = uniqueId
                ? { unique_id: uniqueId }
                : propertyId.startsWith("ad") ? { unique_id: propertyId } : { geo_id: propertyId }
            const addressResponse = await apiHandler.getAddress(addressQuery)
            const addressSlug = addressResponse?.aurl || addressResponse?.address_url || ""

            let path = `${routeBase}/${idForUrl}`
            if (addressSlug) {
                path += `/${encodeURIComponent(addressSlug)}`
            }

            const queryParams = new URLSearchParams()
            if (houseCondition) {
                queryParams.set("house_condition", houseCondition)
            }
            if (removedHomes) {
                queryParams.set("removed_homes", removedHomes)
            }

            const queryString = queryParams.toString()
            if (queryString) {
                path += `?${queryString}`
            }

            this.navigateTo(path)
        } catch (error) {
            console.error("Error navigating to valuation:", error)
            this.navigateTo(`${routeBase}/${uniqueId ?? propertyId}`)
        }
    }

    navigateToArea(areaId: string, areaSlug?: string) {
        let path = `/area/${encodeURIComponent(areaId)}`
        if (areaSlug) {
            path += `/${encodeURIComponent(areaSlug)}`
        }
        this.navigateTo(path)
    }

    async navigateToValuation(propertyId: string, apiHandler: ApiHandler, houseCondition?: string, removedHomes?: string, uniqueId?: string) {
        await this.navigateToValuationRoute(propertyId, apiHandler, "/valuate", houseCondition, removedHomes, uniqueId)
    }
}

// Singleton instance
const navigationService = new NavigationService()
export default navigationService
